/**
 * Cache Key Generator Utility
 *
 * Generates consistent cache keys for task summaries.
 * Ensures cache invalidation and proper key uniqueness.
 */

import type { TaskSummaryRequest } from "@/lib/types/ai";

/**
 * Generate a cache key for a task summary
 * Includes task ID and note count for cache invalidation
 */
export function generateTaskSummaryCacheKey(
  taskId: string,
  noteCount: number
): string {
  return `task-summary-${taskId}-${noteCount}`;
}

/**
 * Generate a cache key for AI summary requests
 * Includes entity type, ID, summary type, and options hash
 */
export function generateAISummaryCacheKey(
  entityType: string,
  entityId: string,
  summaryType: string,
  options?: Record<string, any>
): string {
  const optionsHash = options ? JSON.stringify(options) : "";
  const dateKey = new Date().toISOString().split("T")[0]; // Daily cache invalidation
  const encodedOptions = optionsHash
    ? Buffer.from(optionsHash).toString("base64")
    : "";

  return `ai-summary:${summaryType}:${entityType}:${entityId}:${dateKey}:${encodedOptions}`;
}

/**
 * Validate cache key format
 */
export function isValidCacheKey(key: string): boolean {
  return typeof key === "string" && key.length > 0;
}
